import createDOMPurify from 'dompurify'

import { log } from '../../utils/logger'

const ALLOWED_ATTR = [
    'href',
    'class',
    'style',
    'target',
    'rel',
    'src',
    'alt',
    'id',
    'colspan',
    'rowspan',
    'event-name',
    'value',
    'tabindex',
    'width',
    'height',
    'loading',
    'dir',
    'title',
    'frameborder',
    'allow',
    'allowfullscreen',
]

// Matched exactly, so `player.vimeo.com.evil.tld` can't pass.
const ALLOWED_IFRAME_HOST = 'player.vimeo.com'

const isAllowedEmbed = (src: string | null): boolean => {
    if (!src) {
        return false
    }
    try {
        const url = new URL(src)
        return url.protocol === 'https:' && url.hostname.toLowerCase() === ALLOWED_IFRAME_HOST
    } catch {
        return false
    }
}

const ALLOWED_TAGS = [
    'a',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'small',
    'br',
    'hr',
    'span',
    'sup',
    'sub',
    'p',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'img',
    'ul',
    'ol',
    'li',
    'dl',
    'dt',
    'dd',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'figure',
    'figcaption',
    'colgroup',
    'col',
    'zd-summary-block',
    // Only survives the afterSanitizeElements host check below.
    'iframe',
]

// Dedicated instance so our hooks stay scoped to $sanitizeHtml and don't leak onto other
// DOMPurify.sanitize() callers. Created lazily, once a DOM window exists.
let instance: ReturnType<typeof createDOMPurify> | null = null
const getPurify = () => {
    if (instance) {
        return instance
    }
    const purify = createDOMPurify(window)
    purify.addHook('afterSanitizeElements', (node: Node) => {
        if (!(node instanceof Element) || node.tagName !== 'IFRAME' || isAllowedEmbed(node.getAttribute('src'))) {
            return
        }
        // Push to `removed` so the reporter below catches off-host iframes too.
        purify.removed.push({ element: node })
        node.remove()
    })
    purify.addHook('afterSanitizeAttributes', (node: Node) => {
        if (!(node instanceof Element)) {
            return
        }
        // rel=noopener on target=_blank guards against reverse-tabnabbing; merge so nofollow etc survive.
        if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
            const rels = new Set((node.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean))
            rels.add('noopener')
            rels.add('noreferrer')
            node.setAttribute('rel', [...rels].join(' '))
        }
        // Drop styles that can fetch a resource or beacon (url()/@import) or execute (javascript:/expression()).
        const style = node.getAttribute('style')
        if (style && /javascript:|expression\(|url\(|@import/i.test(style)) {
            node.removeAttribute('style')
        }
    })
    instance = purify
    return purify
}

// Stringify like native v-html rather than dropping non-strings: Vue 3 passes the raw binding (no _s
// wrapper), and callers legitimately bind string arrays (footnote renderer), numbers, etc. Objects have
// no meaningful HTML form, so skip them (null) rather than render [object Object].
const coerceToHtmlString = (value: unknown): string | null => {
    if (typeof value === 'string') {
        return value
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value)
    }
    if (Array.isArray(value)) {
        return value.join(',')
    }
    return null
}

// DOMPurify reports stripping the html/head/body it auto-inserts and any HTML comments; that's
// normalization, not meaningful caller content, so it never counts as a real strip.
const STRUCTURAL_WRAPPERS = new Set(['html', 'head', 'body', '#comment'])

// Names only, never attribute values or text — logs what was stripped, not content.
const removedEntryName = (entry: { element?: Node; attribute?: { name?: string } | null }): string | null => {
    if (entry.attribute) {
        return `@${entry.attribute.name ?? 'attr'}`
    }
    if (entry.element) {
        const name = entry.element.nodeName.toLowerCase()
        return STRUCTURAL_WRAPPERS.has(name) ? null : name
    }
    return 'unknown'
}

// One log per call site per page load; the same v-html re-renders constantly and we only need to
// know it exists.
const reportedLocators = new Set<string>()

// Sanitizes an HTML string for safe v-html / innerHTML rendering: strips scripts, event handlers,
// javascript: URLs and off-host iframes while keeping the light formatting the apps render.
// `locator` is the compile-time call site injected by compilerTransforms.cjs (file:line in Vue 3,
// tag+expr in Vue 2).
export const sanitizeHtml = (value: unknown, locator?: string): string => {
    const html = coerceToHtmlString(value)
    if (html === null) {
        return ''
    }
    const purify = getPurify()
    const clean = purify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
    const removedNames = [...new Set(purify.removed.map(removedEntryName).filter((n: string | null): n is string => n !== null))]
    if (removedNames.length > 0) {
        const key = locator ?? 'unknown'
        if (!reportedLocators.has(key)) {
            reportedLocators.add(key)
            log.warn('v-html sanitizer stripped [%{removedNames}] at %{key}', { removedNames: removedNames.join(', '), key })
        }
    }
    return clean
}
